/**
 * Copyright 2017 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import { configureStore } from "@reduxjs/toolkit";
import { loggedUserReducer } from "openstack-uicore-foundation/lib/security/reducers";
import { persistStore, persistCombineReducers } from "redux-persist";
import storage from "redux-persist/es/storage";
import baseReducer from "./reducers/base-reducer";
import requestListReducer from "./reducers/request-list-reducer";
import showListReducer from "./reducers/show-list-reducer";
import sponsorFormsListReducer from "./reducers/sponsor-forms-list-reducer";
import sponsorOrdersListReducer from "./reducers/sponsor-orders-list-reducer";
import showAccessReducer from "./reducers/show-access-reducer";
import loginReducer from "./reducers/login-reducer";
import requestAccessReducer from "./reducers/request-access-reducer";
import sponsorFormReducer from "./reducers/sponsor-form-reducer";
import checkoutReducer from "./reducers/checkout-reducer";
import sponsorAvailablePagesReducer from "./reducers/sponsor-available-pages-reducer";
import sponsorCurrentPageReducer from "./reducers/sponsor-current-page-reducer";
import sponsorDeadlineListReducer from "./reducers/sponsor-deadline-list-reducer";
import sponsorBadgeScansListReducer from "./reducers/sponsor-badge-scans-list-reducer";
import sponsorBadgeScansReducer from "./reducers/sponsor-badge-scan-reducer";
import sponsorBadgeScansExtraQuestionsList from "./reducers/sponsor-badge-scan-extra-questions-list-reducer";
import sponsorBadgeScansExtraQuestion from "./reducers/sponsor-badge-scan-extra-question-reducer";

// default: localStorage if web, AsyncStorage if react-native

const config = {
  key: "root",
  storage
};

const reducers = persistCombineReducers(config, {
  loggedUserState: loggedUserReducer,
  baseState: baseReducer,
  showListState: showListReducer,
  loginState: loginReducer,
  requestAccessState: requestAccessReducer,
  showAccessState: showAccessReducer,
  requestListState: requestListReducer,
  sponsorFormsListState: sponsorFormsListReducer,
  sponsorFormState: sponsorFormReducer,
  checkoutState: checkoutReducer,
  sponsorOrdersListState: sponsorOrdersListReducer,
  sponsorAvailablePagesState: sponsorAvailablePagesReducer,
  sponsorCurrentPageState: sponsorCurrentPageReducer,
  sponsorDeadlineListState: sponsorDeadlineListReducer,
  sponsorBadgeScansListState: sponsorBadgeScansListReducer,
  sponsorBadgeScansState: sponsorBadgeScansReducer,
  sponsorBadgeScansExtraQuestionsListState: sponsorBadgeScansExtraQuestionsList,
  sponsorBadgeScansExtraQuestionState: sponsorBadgeScansExtraQuestion
});

export const setupStore = (preloadedState = {}) =>
  configureStore({
    reducer: reducers,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"]
        }
      })
  });

const store = setupStore();

const onRehydrateComplete = () => {
  // repopulate access token on global access variable
  window.sessionState = store.getState().loggedUserState.sessionState;
};

export const persistor = persistStore(store, null, onRehydrateComplete);
export default store;
